import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AppHeader, SourceNote } from "@/components/Layout";
import { PlanPicker } from "@/components/PlanPicker";
import { api, errMsg, tokens, openStripe, STATES, ALLERGENS, ALERT_TYPES } from "@/lib/api";
import { useSeo } from "@/lib/seo";

const Pill = ({ selected, onClick, children, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick}
    className={`rounded-full border px-4 py-2 text-sm font-medium transition-all duration-200 ${
      selected
        ? "border-slate-900 bg-slate-900 text-white shadow-sm"
        : "border-slate-200 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50"
    }`}>{children}</button>
);

export default function Settings() {
  useSeo({ title: "Your alert settings", description: "Manage your state, allergens and SMS alerts.", path: "/settings", noindex: true });
  const nav = useNavigate();
  const [u, setU] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!tokens.get()) { nav("/join"); return; }
    api.get("/me").then(setU).catch(() => { tokens.clear(); nav("/join"); });
  }, [nav]);

  // The section only exists once /me has loaded, so honour the hash after that.
  useEffect(() => {
    if (!u || window.location.hash !== "#sms-alerts") return;
    document.getElementById("sms-alerts")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [u]);

  if (!u) return (<div className="min-h-screen bg-white"><AppHeader /><p className="p-10 text-slate-500">Loading...</p></div>);

  const patch = async (body) => {
    setBusy(true);
    try {
      const next = await api.put("/me", body);
      setU(next);
      toast.success("Preferences updated");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const toggleType = (key) => {
    if (key === "all") return patch({ alert_types: ["all"] });
    const base = u.alert_types.filter((t) => t !== "all");
    const next = base.includes(key) ? base.filter((t) => t !== key) : [...base, key];
    return patch({ alert_types: next.length ? next : ["all"] });
  };

  const toggleAllergy = (a) => {
    const next = u.allergies.includes(a) ? u.allergies.filter((x) => x !== a) : [...u.allergies, a];
    return patch({ allergies: next });
  };

  const unsubscribe = async () => {
    setBusy(true);
    try { await api.post("/me/unsubscribe", {}); nav("/unsubscribed"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const deleteAccount = async () => {
    if (!window.confirm("Delete your account and stop all alerts?")) return;
    setBusy(true);
    try { await api.del("/me"); tokens.clear(); nav("/"); toast.success("Account deleted"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const Section = ({ title, children, sub, id }) => (
    <section id={id} className="scroll-mt-24 rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_1px_3px_rgba(15,23,42,0.04)] sm:p-7">
      <h2 className="font-display text-lg font-semibold tracking-tight text-slate-900">{title}</h2>
      {sub && <p className="mt-1.5 text-sm text-slate-500">{sub}</p>}
      <div className="mt-5">{children}</div>
    </section>
  );

  return (
    <div className="min-h-screen bg-white">
      <AppHeader />
      <div className="mx-auto max-w-2xl space-y-5 px-5 py-10 sm:space-y-6 sm:py-14">
        <div>
          <p className="label-caps">Your account</p>
          <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tighter text-slate-900 sm:text-5xl">
            Settings
          </h1>
        </div>

        {!u.subscription_active && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6" data-testid="settings-no-subscription-notice">
            <p className="font-display font-semibold text-amber-900">You don't have an active subscription</p>
            <p className="mt-1.5 text-sm text-amber-800">
              Browsing recalls stays free. Pick a plan below to start receiving SMS alerts.
            </p>
          </div>
        )}

        <Section title="Mobile">
          <p className="font-mono text-lg text-slate-900" data-testid="settings-mobile">{u.mobile_display}</p>
          <p className="mt-1 text-xs text-slate-500">{u.mobile_verified ? "Verified" : "Not verified"}</p>
        </Section>

        <Section title="State">
          <div className="flex flex-wrap gap-2">
            {STATES.map((s) => (
              <Pill key={s} testid={`settings-state-${s}`} selected={u.state === s} onClick={() => patch({ state: s })}>{s}</Pill>
            ))}
          </div>
        </Section>

        <Section title="Coverage">
          <div className="flex flex-wrap gap-2">
            <Pill testid="settings-coverage-all" selected={u.coverage === "all"} onClick={() => patch({ coverage: "all" })}>Australia-wide</Pill>
            <Pill testid="settings-coverage-state" selected={u.coverage === "state_national"} onClick={() => patch({ coverage: "state_national" })}>My state + National</Pill>
          </div>
        </Section>

        <Section title="Alert types">
          <div className="flex flex-wrap gap-2">
            {ALERT_TYPES.map((t) => (
              <Pill key={t.key} testid={`settings-type-${t.key}`} selected={u.alert_types.includes(t.key)} onClick={() => toggleType(t.key)}>{t.label}</Pill>
            ))}
          </div>
        </Section>

        <Section title="Priority allergies" sub="We use these only to prioritise relevant recall alerts.">
          <div className="flex flex-wrap gap-2">
            {ALLERGENS.map((a) => (
              <Pill key={a} testid={`settings-allergy-${a.toLowerCase().replace(/[^a-z]/g, "-")}`} selected={u.allergies.includes(a)} onClick={() => toggleAllergy(a)}>{a}</Pill>
            ))}
          </div>
        </Section>

        <Section title="SMS alerts" id="sms-alerts">
          {u.subscription_active ? (
            <>
              <div className="mb-4 flex flex-wrap items-center gap-3" data-testid="subscription-active">
                <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-700">
                  Subscription active
                </span>
                {u.subscription_plan && (
                  <span className="text-sm text-slate-600">{u.subscription_plan.replace("alerts_", "")}</span>
                )}
                <button
                  data-testid="manage-subscription-btn"
                  disabled={busy}
                  onClick={async () => {
                    setBusy(true);
                    try {
                      const r = await api.post("/billing/portal", { origin_url: window.location.origin });
                      if (!openStripe(r.portal_url)) {
                        toast.error("Your browser blocked the billing window. Allow pop-ups and try again.");
                      }
                      setBusy(false);
                    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
                  }}
                  className="rounded-full border border-slate-300 px-5 py-2 text-sm font-medium text-slate-800 transition-colors hover:bg-slate-50"
                >
                  Manage subscription
                </button>
              </div>
              <button data-testid="settings-sms-toggle" disabled={busy} onClick={() => patch({ sms_enabled: !u.sms_enabled })}
                className={`rounded-full px-6 py-3 text-sm font-semibold text-white transition-colors ${u.sms_enabled ? "bg-slate-900 hover:bg-slate-800" : "bg-red-700 hover:bg-red-800"}`}>
                {u.sms_enabled ? "On - tap to turn off" : "Off - tap to turn on"}
              </button>
            </>
          ) : (
            <div className="space-y-4" data-testid="subscription-inactive">
              <p className="text-sm text-slate-600">
                Browsing recalls is free. Subscribe to get a text the moment a relevant Australian recall is published.
              </p>
              <PlanPicker ctaLabel="Subscribe to get SMS alerts" />
            </div>
          )}
        </Section>

        <Section title="Stop or delete">
          <div className="flex flex-wrap gap-3">
            <button data-testid="settings-unsubscribe" disabled={busy} onClick={unsubscribe}
              className="rounded-full border border-slate-300 px-6 py-3 text-sm font-medium text-slate-800 transition-colors hover:bg-slate-50">
              Unsubscribe from SMS
            </button>
            <button data-testid="settings-delete-account" disabled={busy} onClick={deleteAccount}
              className="rounded-full bg-red-700 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-red-800">
              Delete account
            </button>
          </div>
        </Section>
        <SourceNote />
      </div>
    </div>
  );
}
